import React from "react";
import Moment from "moment";
import { Box, Typography } from "@mui/material";
import ImageThumbnail from "../../imgs/thumbnail";

const AUDIO_IN_PATH = /\.(m4a|mp3|aac|wav|ogg)(\?|#|$)/i;

/**
 * @param {object} item
 * @returns {'audio' | 'image'}
 */
function getMediaKind(item) {
  const probe = `${item.media_src || ""} ${item.response || ""}`.toLowerCase();
  if (AUDIO_IN_PATH.test(probe)) return "audio";
  return "image";
}

function humanAudioLabel(item) {
  const raw = (item.response || "").trim();
  if (/background-audio/i.test(raw)) return "Background audio";
  return "Audio recording";
}

function sortByResponseTime(entries) {
  return [...entries].sort((a, b) => {
    const ta = a.response_time ? new Date(a.response_time).getTime() : 0;
    const tb = b.response_time ? new Date(b.response_time).getTime() : 0;
    return ta - tb;
  });
}

function truncateWithEllipsis(value, maxLength = 20) {
  const text = value != null ? String(value).trim() : "";
  if (!text) return null;
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

/**
 * Renders canvass script responses for a single event: text lines, images, and audio
 * as separate blocks (preserves order by response_time).
 */
export default function TimelineResponseDisplay({
  responses,
  statusFallback,
}) {
  const list = Array.isArray(responses) ? responses : [];
  const visible = list.filter(
    (r) =>
      (r.response != null && String(r.response).trim() !== "") || !!r.media_src
  );

  if (!visible.length) {
    return <span>{statusFallback || "--"}</span>;
  }

  const sorted = sortByResponseTime(visible);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.375,
        minWidth: 0,
        width: "100%",
        py: 0,
        my: 0,
      }}
    >
      {sorted.map((item, idx) => {
        const hasMedia = !!item.media_src;
        const text = item.response != null ? String(item.response).trim() : "";
        const kind = hasMedia ? getMediaKind(item) : null;
        const timeLabel = item.response_time
          ? Moment(item.response_time).format("LT")
          : null;
        const elementNameFull =
          item.name != null ? String(item.name).trim() : "";
        const elementName = truncateWithEllipsis(elementNameFull);

        return (
          <Box
            key={item.id != null ? String(item.id) : `resp-${idx}`}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              columnGap: 0.5,
              rowGap: 0.125,
              minWidth: 0,
              py: 0.125,
            }}
          >
            {(timeLabel || elementName) && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  columnGap: 0.5,
                  rowGap: 0.125,
                  flex: "0 1 auto",
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                {timeLabel && (
                  <Typography
                    variant="caption"
                    component="span"
                    color="text.secondary"
                    sx={{
                      fontVariantNumeric: "tabular-nums",
                      lineHeight: 1.2,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {timeLabel}
                  </Typography>
                )}
                {elementName && (
                  <Typography
                    variant="caption"
                    component="span"
                    color="text.secondary"
                    title={
                      elementNameFull.length > 20 ? elementNameFull : undefined
                    }
                    sx={{
                      fontWeight: 500,
                      lineHeight: 1.2,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {elementName}
                  </Typography>
                )}
              </Box>
            )}
            <Box
              sx={{
                width: "100%",
                paddingHorizontal: 2,
                marginHorizontal: 2,
              }}
            >
              {!hasMedia && text !== "" && (
                <Typography
                  component="div"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.85em",
                    lineHeight: 1.25,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {text}
                </Typography>
              )}

              {hasMedia && kind === "image" && (
                <Box
                  className="flx wrap"
                  sx={{ gap: 0.5, alignItems: "flex-start" }}
                >
                  <ImageThumbnail
                    src={item.media_thumb_src || item.media_src}
                    largeSrc={item.media_src}
                    thumbnailFallbackSrc={
                      item.media_thumb_src ? item.media_src : undefined
                    }
                  />
                </Box>
              )}

              {hasMedia && kind === "audio" && (
                <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mb: 0.25,
                      lineHeight: 1.2,
                      overflowWrap: "anywhere",
                    }}
                    title={text || undefined}
                  >
                    {humanAudioLabel(item)}
                  </Typography>
                  <audio
                    controls
                    src={item.media_src}
                    preload="metadata"
                    style={{ width: "100%", maxWidth: "min(280px, 100%)" }}
                  >
                    <source src={item.media_src} type="audio/mpeg" />
                    <source src={item.media_src} type="audio/mp4" />
                    Your browser does not support the audio element.
                  </audio>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
